import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ScrollText, ShieldAlert } from "lucide-react";
import { EASE } from "@/components/Motion";
import { Logo } from "@/components/Logo";

export default function TermsPage() {
  const [content, setContent] = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content`)
      .then((r) => r.json())
      .then(setContent)
      .catch(() => {});
  }, []);

  const terms = content?.terms_text || "";
  const blocks = terms.split("\n\n").filter((b) => b.trim());

  return (
    <div className="relative min-h-screen bg-ink text-neutral-100" data-testid="terms-page">
      <div className="pointer-events-none absolute -top-24 left-1/3 h-[420px] w-[420px] rounded-full bg-gold/[0.06] blur-[130px]" />
      <div className="noise-overlay pointer-events-none fixed inset-0 opacity-[0.05]" />

      <div className="relative mx-auto max-w-3xl px-6 py-14 sm:py-20">
        <div className="flex items-center justify-between">
          <Link to="/" data-testid="terms-logo" className="flex items-center gap-3">
            <Logo className="h-10 w-10 rounded-xl object-cover ring-1 ring-gold/40" />
            <span className="text-sm font-bold tracking-[0.2em]">
              BEATKING<span className="text-gold">SMM</span>
            </span>
          </Link>
          <Link
            to="/"
            data-testid="terms-back-link"
            className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] text-neutral-500 transition-colors hover:text-goldlight"
          >
            <ArrowLeft size={13} /> BACK
          </Link>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mt-16"
        >
          <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.35em] text-gold/80">
            <ScrollText size={14} /> The fine print, in gold
          </div>
          <h1 className="mt-6 font-serif text-5xl italic text-neutral-50 sm:text-6xl" data-testid="terms-heading">
            Terms &amp; Conditions
          </h1>
          <p className="mt-4 font-mono text-[11px] tracking-[0.2em] text-neutral-500">
            LAST UPDATED · SEPTEMBER 2026
          </p>
          <div className="mt-8 h-px w-32 bg-gradient-to-r from-gold to-transparent" />
        </motion.div>

        {content?.refund_policy && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}
            className="mt-10 rounded-2xl border border-gold/25 bg-gold/[0.05] p-6"
            data-testid="terms-refund-highlight"
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold/80">Refund policy</div>
            <p className="mt-2 text-sm leading-relaxed text-neutral-300">{content.refund_policy}</p>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.25 }}
          className="mt-12 space-y-8"
          data-testid="terms-body"
        >
          {blocks.length === 0 && (
            <p className="text-sm text-neutral-500">Terms are being finalized. Check back shortly.</p>
          )}
          {blocks.map((b, i) => {
            const trimmed = b.trim();
            const isHeading = /^\d+\.\s+[A-Z]/.test(trimmed);
            return isHeading ? (
              <h2 key={i} className="pt-4 text-xl font-bold tracking-tight text-neutral-50">
                {trimmed}
              </h2>
            ) : (
              <p key={i} className="text-sm leading-relaxed text-neutral-400 sm:text-base">
                {trimmed}
              </p>
            );
          })}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-16 flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.02] p-6"
          data-testid="terms-disclaimer"
        >
          <ShieldAlert size={18} className="mt-0.5 shrink-0 text-gold" />
          <p className="text-xs leading-relaxed text-neutral-500">
            BEATKINGSMM is not a trading, investing or money-doubling company. Every rupee you spend
            buys real marketing, deal-making or sourcing work — never a promise of returns.
          </p>
        </motion.div>

        <p className="mt-10 text-center font-mono text-[10px] tracking-[0.25em] text-neutral-700">
          © 2026 BEATKINGSMM · SINCE 2018
        </p>
      </div>
    </div>
  );
}
